import React, { useEffect, useMemo, useRef, ElementRef } from 'react'
import { Field, FieldRenderProps } from 'react-final-form'
import { getClientTimezone } from '@chilipiper/date-time'
import {
  FieldProps,
  getFieldAdapterError,
  UncontrolledFieldProps,
} from '../helpers/final-form/types'
import { Select } from '../select/Select'
import { State as SelectState } from '../select/types'
import { SelectProps, useSelectWithCategoryControl } from '../select'
import { getTimeZoneOptions } from './helpers'

type TimezoneSelectProps = SelectProps & {
  locale?: string
  showFullTimezone?: boolean
}

type SelectRef = ElementRef<typeof Select>

/**
 * This component blocks browser renderfor at least 200ms. Ideally it shouldn't be used on initial
 * render of the page
 */
export const TimezoneSelect = ({
  locale,
  value = '',
  showFullTimezone = false,
  onChange,
  ...others
}: TimezoneSelectProps) => {
  const selectRef = useRef<SelectRef>(null)
  const timeZoneOptions = useMemo(
    () => getTimeZoneOptions(locale, showFullTimezone),
    [locale, showFullTimezone]
  )

  const { renderedOptions, setFilter } = useSelectWithCategoryControl({
    options: timeZoneOptions,
    renderCategoryLabel: category => category.replace('_category', ''),
    isExpandableCategories: true,
    debounceDuration: 250,
  })

  const handleShortcut = ({ ctrlKey, key }: KeyboardEvent) => {
    if (ctrlKey && key === 'd') {
      selectRef.current?.setState((prevState: SelectState) => ({
        ...prevState,
        showing: !prevState.showing,
      }))
    }
  }

  useEffect(() => {
    if (value === '') {
      onChange(getClientTimezone())
    }
    document.addEventListener('keydown', handleShortcut)
    return () => document.removeEventListener('keydown', handleShortcut)
  }, [])

  return (
    <Select
      ref={selectRef}
      search={setFilter}
      labelStyles={{
        px: 2,
        pl: 3,
      }}
      label='Timezone'
      id='timezone-select'
      h={10}
      {...others}
      onChange={onChange}
      value={value}
    >
      {renderedOptions}
    </Select>
  )
}

const TimezoneSelectAdapter = (props: FieldRenderProps<TimezoneSelectProps['value'], any>) => {
  const { input, meta, ...rest } = props
  const passedProps = rest as UncontrolledFieldProps<TimezoneSelectProps>

  return (
    <TimezoneSelect
      {...input}
      {...passedProps}
      onChange={value => input.onChange(value)}
      error={getFieldAdapterError(meta)}
    />
  )
}

type FinalDatePickerProps = UncontrolledFieldProps<TimezoneSelectProps> &
  FieldProps<TimezoneSelectProps['value'], any>

export const FinalTimezoneSelect = (props: FinalDatePickerProps) => (
  <Field<TimezoneSelectProps['value'], any> component={TimezoneSelectAdapter} {...props} />
)
